import React, { memo } from 'react';
import type { StepNode, OptionsItem, ProductOffer, ScreenAnimation } from '@segmently/schema-models/v2';
import { ScreenAnimationProvider } from '../animations';
import type { OnActionCallback, OnEventCallback } from '../types/events';
import type { TemplateVariables } from '../utils/templateParser';
import type { PreviewMode } from '../types/previewMode';
import type { ButtonVisibilityState } from '../utils/buttonVisibility';
import type {
  CustomScreenButtonActionRequest,
  CustomScreenOptionActionRequest,
  CustomScreenRenderMode,
  LegacyInitData,
} from '../types/customScreenBridge.types';
import type { LegalLinksEventContext } from './shared/LegalLinks';
import styles from './V2ScreenView.module.css';

// Screen type components
import { HeroContent } from './screens/HeroContent';
import { ProgressSteps } from './screens/ProgressSteps';
import { TextInput } from './screens/TextInput';
import { ListSinglePick } from './screens/ListSinglePick';
import { ListMultiPick } from './screens/ListMultiPick';
import { GridSinglePick } from './screens/GridSinglePick';
import { GridMultiPick } from './screens/GridMultiPick';
import { HeroList } from './screens/HeroList';
import { Carousel } from './screens/Carousel';
import { WheelPicker } from './screens/WheelPicker';
import { WebEmbed } from './screens/WebEmbed';
import { Paywall } from './screens/Paywall';
import { FlexibleLayout } from './screens/FlexibleLayout';

// ============================================================================
// Selection Step Kind Constants
// ============================================================================

export const MULTI_PICK_STEP_KINDS = ['ListMultiPick', 'GridMultiPick'] as const;
export const SINGLE_PICK_STEP_KINDS = ['ListSinglePick', 'GridSinglePick'] as const;

export function isMultiPickStep(kind: string): boolean {
  return MULTI_PICK_STEP_KINDS.includes(kind as (typeof MULTI_PICK_STEP_KINDS)[number]);
}

export function isSinglePickStep(kind: string): boolean {
  return SINGLE_PICK_STEP_KINDS.includes(kind as (typeof SINGLE_PICK_STEP_KINDS)[number]);
}

// ============================================================================
// Props Interface
// ============================================================================

export interface V2ScreenViewProps {
  /** StepNode with screenType discriminator */
  step: StepNode;
  /** Locale for l10n content resolution (e.g., "en-US") */
  locale: string;
  /** Variables for template substitution */
  variables?: TemplateVariables;
  /** Per-product template variables (productId -> variables) for Paywall */
  itemVariablesMap?: Record<string, TemplateVariables>;
  /** Pre-resolved screen link values from FunnelEngine */
  resolvedScreenValues?: Record<string, string>;
  /** Callback for action events */
  onAction?: OnActionCallback;
  /** Callback for all screen events */
  onEvent?: OnEventCallback;
  /** Enable edit mode with visual hover feedback */
  editMode?: boolean;
  /** Preview mode for asset quality control */
  previewMode: PreviewMode;
  /**
   * When true, we're in the server-render pass (react-dom/server).
   * Threaded into WebEmbed so the shadow-dom branch emits a `<template shadowrootmode="open">`
   * block with the Custom HTML baked in — the browser materialises the ShadowRoot on parse,
   * eliminating the post-hydration empty-div FOUC that pure CSR `attachShadow` produces.
   */
  ssrMode?: boolean;

  // ========== Selection State Props ==========

  /** Selected indices for multi-pick screens (ListMultiPick, GridMultiPick) */
  selectedIndices?: Set<number>;
  /** Selected index for single-pick screens (ListSinglePick, GridSinglePick) */
  selectedIndex?: number | null;
  /** Callback when multi-pick selection changes */
  onMultipleSelectionChange?: (indices: Set<number>) => void;
  /** Callback when single-pick selection changes */
  onSingleSelectionChange?: (index: number | null) => void;

  // ========== Field Input State Props ==========

  /** Field input value for TextInput */
  fieldValue?: string;
  /** Callback when field value changes */
  onFieldValueChange?: (value: string) => void;

  // ========== CustomScreen Value Change Props ==========

  /** Callback when WebEmbed's sdk.setValue() is called */
  onCustomScreenValueChange?: (key: string, value: unknown) => void;

  // ========== Animation Control Props ==========

  /** External control: is animation playing (from editor Play button) */
  isAnimationPlaying?: boolean;
  /** Callback when animation completes */
  onAnimationComplete?: () => void;
  /**
   * Entrance-only completion (edit-mode Play): fires when the screen's entrance ends, for
   * every screen type — Carousel/ProgressSteps do not hold it for their cycle.
   */
  onEntranceComplete?: () => void;
  /**
   * Flow-level entrance fallback (FlowDocument.defaultScreenAnimation).
   * Lowest priority in the in-screen animation chain:
   * content.screenAnimation > content.animated:true (fadeIn) > this.
   */
  defaultScreenAnimation?: ScreenAnimation | null;
  /**
   * False while a screen-to-screen transition is still running (threaded from
   * web-shell). Gates `onScreenEnter`-triggered element animations.
   * Undefined (editor previews) means "entered".
   */
  screenEntered?: boolean;
  /**
   * True on the screen the static (SSR) page painted, for the client mount that
   * replaces it (threaded from web-shell): mount-driven entrances render their
   * final state — no replay on the takeover (AMV-AC-036).
   */
  skipMountEntrances?: boolean;
  /** The published render's first screen: its entrances render running from first paint (FSE; web-shell threads it) */
  ssrEntrance?: boolean;

  // ========== Payment State Props ==========

  /** Loading state for paywall screens */
  isLoading?: boolean;
  /** Currently selected subscription index */
  selectedSubscriptionIndex?: number | null;
  /** Callback when subscription selection changes */
  onSubscriptionChange?: (index: number | null) => void;
  /** Callback when footer link is clicked (privacy, terms, restore) */
  onLinkClick?: (context: LegalLinksEventContext) => void;

  // ========== FlexibleLayout Section Props ==========

  /** Per-section selected option indices for FlexibleLayout OptionsList sections (sectionId → Set<index>) */
  sectionSelections?: Map<string, Set<number>>;
  /** Callback when an option is selected/deselected within a FlexibleLayout section */
  onSectionOptionSelect?: (sectionId: string, item: OptionsItem, index: number, selected: boolean) => void;
  /** Per-section selected product index for FlexibleLayout ProductCatalog sections (sectionId → index) */
  productSelections?: Map<string, number>;
  /** Callback when a product is selected in a FlexibleLayout ProductCatalog section */
  onSectionProductSelect?: (sectionId: string, item: ProductOffer, index: number) => void;
  /** Per-section button visibility state for FlexibleLayout Button sections (sectionId → { disabled, hidden }) */
  sectionButtonStates?: Record<string, ButtonVisibilityState>;
  /** Per-section text field values for FlexibleLayout TextField sections (sectionId → value) */
  sectionTextFieldValues?: Map<string, string>;
  /** Per-section text field errors for FlexibleLayout TextField sections (sectionId → error) */
  sectionTextFieldErrors?: Map<string, string | null>;
  /** Callback when text field value changes in a FlexibleLayout TextField section */
  onSectionTextFieldChange?: (sectionId: string, value: string) => void;
  /** Callback when text field loses focus in a FlexibleLayout TextField section */
  onSectionTextFieldBlur?: (sectionId: string) => void;
  /** Raw HTML per section (section.id → html) for FlexibleLayout CustomEmbed sections */
  embedHtmlMap?: Record<string, string>;
  /** Init data per section (section.id → LegacyInitData) for FlexibleLayout CustomEmbed sections */
  embedInitDataMap?: Record<string, LegacyInitData>;
  /** Callback when a FlexibleLayout CustomEmbed section requests forward navigation */
  onSectionEmbedNavigate?: (sectionId: string, data: Record<string, unknown>) => void;
  /** Callback when a FlexibleLayout CustomEmbed section requests back navigation */
  onSectionEmbedBack?: () => void;
  /** Callback for analytics events from FlexibleLayout CustomEmbed sections */
  onSectionEmbedTrackEvent?: (name: string, params?: Record<string, unknown>) => void;
  /** Callback when a FlexibleLayout CustomEmbed section calls sdk.setValue() */
  onSectionEmbedValueChange?: (key: string, value: unknown) => void;
  /** Callback when a FlexibleLayout CustomEmbed section calls sdk.setVariable() */
  onSectionEmbedSetVariable?: (variableId: string, value: unknown) => void;
  /** Callback when a FlexibleLayout CustomEmbed section triggers an option action on a child section */
  onSectionEmbedTriggerOptionAction?: (embedSectionId: string, request: CustomScreenOptionActionRequest) => void;
  /** Callback when a FlexibleLayout CustomEmbed section triggers a Button child section action */
  onSectionEmbedTriggerButtonAction?: (embedSectionId: string, request: CustomScreenButtonActionRequest) => void;
  /** Callback when a FlexibleLayout CustomEmbed section calls sdk.selectProduct() */
  onSectionEmbedSelectProduct?: (embedSectionId: string, childSectionId: string, productIndex: number, productId: string) => void;
  /** Callback when a FlexibleLayout CustomEmbed section calls sdk.purchaseProduct() */
  onSectionEmbedPurchaseProduct?: (embedSectionId: string, requestId: string, productId: string, childSectionId?: string) => void;
  /** Purchase result to push back to FlexibleLayout CustomEmbed iframe */
  sectionEmbedPurchaseResult?: { requestId: string; status: 'success' | 'cancel' | 'error'; error?: string; _ts: number } | null;

  // ========== WebEmbed iframe Mode Props ==========

  /** Render mode for WebEmbed HTML content */
  customScreenRenderMode?: CustomScreenRenderMode;
  /** Init data for WebEmbed iframe mode */
  customScreenInitData?: LegacyInitData;
  /** Callback when WebEmbed iframe requests forward navigation */
  onCustomScreenNavigate?: (data: Record<string, unknown>) => void;
  /** Callback when WebEmbed iframe requests back navigation */
  onCustomScreenBack?: () => void;
  /** Callback for analytics events from WebEmbed iframe */
  onCustomScreenTrackEvent?: (name: string, params?: Record<string, unknown>) => void;
  /** Callback when WebEmbed iframe sets a variable via sdk.setVariable() */
  onCustomScreenSetVariable?: (variableId: string, value: unknown) => void;
  /** Callback when standalone WebEmbed triggers an option action on a child section */
  onCustomScreenTriggerOptionAction?: (request: CustomScreenOptionActionRequest) => void;
  /** Callback when standalone WebEmbed triggers a Button child section action */
  onCustomScreenTriggerButtonAction?: (request: CustomScreenButtonActionRequest) => void;
  /** Variable update broadcast for cross-section reactivity */
  lastVariableUpdate?: { variableId: string; value: unknown; sourceIframeId?: string; _ts: number } | null;
  /** Callback when standalone WebEmbed calls sdk.purchaseProduct() */
  onCustomScreenPurchaseProduct?: (requestId: string, productId: string, childSectionId?: string) => void;
  /** Purchase result to push back to standalone WebEmbed iframe */
  customScreenPurchaseResult?: { requestId: string; status: 'success' | 'cancel' | 'error'; error?: string; _ts: number } | null;
}

export const V2ScreenView = memo(function V2ScreenView({
  step,
  locale,
  ssrMode,
  variables,
  itemVariablesMap,
  resolvedScreenValues,
  onAction,
  onEvent,
  editMode = false,
  previewMode,
  // Selection state
  selectedIndices = new Set<number>(),
  selectedIndex = null,
  onMultipleSelectionChange,
  onSingleSelectionChange,
  // Field input
  fieldValue = '',
  onFieldValueChange,
  // CustomScreen value change
  onCustomScreenValueChange,
  // Animation control
  isAnimationPlaying = false,
  onAnimationComplete,
  onEntranceComplete,
  defaultScreenAnimation,
  screenEntered,
  skipMountEntrances,
  ssrEntrance,
  // Payment state
  isLoading = false,
  selectedSubscriptionIndex,
  onSubscriptionChange,
  onLinkClick,
  // FlexibleLayout section state
  sectionSelections,
  onSectionOptionSelect,
  productSelections,
  onSectionProductSelect,
  sectionButtonStates,
  sectionTextFieldValues,
  sectionTextFieldErrors,
  onSectionTextFieldChange,
  onSectionTextFieldBlur,
  embedHtmlMap,
  embedInitDataMap,
  onSectionEmbedNavigate,
  onSectionEmbedBack,
  onSectionEmbedTrackEvent,
  onSectionEmbedValueChange,
  onSectionEmbedSetVariable,
  onSectionEmbedTriggerOptionAction,
  onSectionEmbedTriggerButtonAction,
  onSectionEmbedSelectProduct,
  onSectionEmbedPurchaseProduct,
  sectionEmbedPurchaseResult,
  // WebEmbed iframe mode
  customScreenRenderMode,
  customScreenInitData,
  onCustomScreenNavigate,
  onCustomScreenBack,
  onCustomScreenTrackEvent,
  onCustomScreenSetVariable,
  onCustomScreenTriggerOptionAction,
  onCustomScreenTriggerButtonAction,
  lastVariableUpdate,
  onCustomScreenPurchaseProduct,
  customScreenPurchaseResult,
}: V2ScreenViewProps): React.ReactElement {
  const screen = ((): React.ReactElement => {
  switch (step.screenType) {
    case 'HeroContent':
      return (
        <HeroContent
          step={step}
          locale={locale}
          variables={variables}
          resolvedScreenValues={resolvedScreenValues}
          onAction={onAction}
          onEvent={onEvent}
          editMode={editMode}
          previewMode={previewMode}
        />
      );

    case 'ProgressSteps':
      return (
        <ProgressSteps
          step={step}
          locale={locale}
          variables={variables}
          resolvedScreenValues={resolvedScreenValues}
          onAction={onAction}
          onEvent={onEvent}
          editMode={editMode}
          previewMode={previewMode}
          isAnimationPlaying={isAnimationPlaying}
          onAnimationComplete={onAnimationComplete}
        />
      );

    case 'TextInput':
      return (
        <TextInput
          step={step}
          locale={locale}
          variables={variables}
          resolvedScreenValues={resolvedScreenValues}
          onAction={onAction}
          onEvent={onEvent}
          editMode={editMode}
          previewMode={previewMode}
          fieldValue={fieldValue}
          onFieldValueChange={onFieldValueChange}
        />
      );

    case 'ListSinglePick':
      return (
        <ListSinglePick
          step={step}
          locale={locale}
          variables={variables}
          resolvedScreenValues={resolvedScreenValues}
          onAction={onAction}
          onEvent={onEvent}
          editMode={editMode}
          previewMode={previewMode}
          selectedIndex={selectedIndex}
          onSelectionChange={onSingleSelectionChange}
        />
      );

    case 'ListMultiPick':
      return (
        <ListMultiPick
          step={step}
          locale={locale}
          variables={variables}
          resolvedScreenValues={resolvedScreenValues}
          onAction={onAction}
          onEvent={onEvent}
          editMode={editMode}
          previewMode={previewMode}
          selectedIndices={selectedIndices}
          onSelectionChange={onMultipleSelectionChange}
        />
      );

    case 'GridSinglePick':
      return (
        <GridSinglePick
          step={step}
          locale={locale}
          variables={variables}
          resolvedScreenValues={resolvedScreenValues}
          onAction={onAction}
          onEvent={onEvent}
          editMode={editMode}
          previewMode={previewMode}
          selectedIndex={selectedIndex}
          onSelectionChange={onSingleSelectionChange}
        />
      );

    case 'GridMultiPick':
      return (
        <GridMultiPick
          step={step}
          locale={locale}
          variables={variables}
          resolvedScreenValues={resolvedScreenValues}
          onAction={onAction}
          onEvent={onEvent}
          editMode={editMode}
          previewMode={previewMode}
          selectedIndices={selectedIndices}
          onSelectionChange={onMultipleSelectionChange}
        />
      );

    case 'HeroList':
      return (
        <HeroList
          step={step}
          locale={locale}
          variables={variables}
          resolvedScreenValues={resolvedScreenValues}
          onAction={onAction}
          onEvent={onEvent}
          editMode={editMode}
          previewMode={previewMode}
        />
      );

    case 'Carousel':
      return (
        <Carousel
          step={step}
          locale={locale}
          variables={variables}
          resolvedScreenValues={resolvedScreenValues}
          onAction={onAction}
          onEvent={onEvent}
          editMode={editMode}
          previewMode={previewMode}
          isAnimationPlaying={isAnimationPlaying}
          onAnimationComplete={onAnimationComplete}
        />
      );

    case 'WheelPicker':
      return (
        <WheelPicker
          step={step}
          locale={locale}
          variables={variables}
          resolvedScreenValues={resolvedScreenValues}
          onAction={onAction}
          onEvent={onEvent}
          editMode={editMode}
          previewMode={previewMode}
        />
      );

    case 'WebEmbed': {
      // V2.16: html is now a typed field on EmbedConfig
      // Fallback to legacy _customScreenDescription for pre-2.16 Firestore data
      const embedHtml = step.content?.embed?.html ?? (step as any)._customScreenDescription as string | undefined;
      // Key includes childSections hash so editing data sources (add/remove/rename/edit text)
      // triggers WebEmbed remount → scripts re-execute with fresh SDK data.
      // Uses JSON.stringify for deep equality — onChange fires on blur/Tab, not every keystroke.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const childSections = (step.content?.embed as any)?.childSections;
      const embedKey = `${step.id}_${childSections ? JSON.stringify(childSections) : ''}`;
      return (
        <WebEmbed
          key={embedKey}
          step={step}
          locale={locale}
          ssrMode={ssrMode}
          variables={variables}
          resolvedScreenValues={resolvedScreenValues}
          onAction={onAction}
          onEvent={onEvent}
          editMode={editMode}
          previewMode={previewMode}
          htmlContent={embedHtml}
          onValueChange={onCustomScreenValueChange}
          renderMode={customScreenRenderMode}
          initData={customScreenInitData}
          onNavigate={onCustomScreenNavigate}
          onBack={onCustomScreenBack}
          onTrackEvent={onCustomScreenTrackEvent}
          onSetVariable={onCustomScreenSetVariable}
          onTriggerOptionAction={onCustomScreenTriggerOptionAction}
          onTriggerButtonAction={onCustomScreenTriggerButtonAction}
          lastVariableUpdate={lastVariableUpdate}
          onPurchaseProduct={onCustomScreenPurchaseProduct}
          lastPurchaseResult={customScreenPurchaseResult}
        />
      );
    }

    case 'Paywall':
      return (
        <Paywall
          step={step}
          locale={locale}
          variables={variables}
          itemVariablesMap={itemVariablesMap}
          resolvedScreenValues={resolvedScreenValues}
          onAction={onAction}
          onEvent={onEvent}
          editMode={editMode}
          previewMode={previewMode}
          isLoading={isLoading}
          selectedSubscriptionIndex={selectedSubscriptionIndex}
          onSubscriptionChange={onSubscriptionChange}
          onLinkClick={onLinkClick}
        />
      );

    case 'FlexibleLayout':
      return (
        <FlexibleLayout
          ssrMode={ssrMode}
          step={step}
          locale={locale}
          variables={variables}
          itemVariablesMap={itemVariablesMap}
          resolvedScreenValues={resolvedScreenValues}
          onAction={onAction}
          onEvent={onEvent}
          editMode={editMode}
          previewMode={previewMode}
          selectedProductIndexMap={productSelections}
          onSectionProductSelect={onSectionProductSelect}
          selectedOptionIndicesMap={sectionSelections}
          onSectionOptionSelect={onSectionOptionSelect}
          sectionButtonStates={sectionButtonStates}
          sectionTextFieldValues={sectionTextFieldValues}
          sectionTextFieldErrors={sectionTextFieldErrors}
          onSectionTextFieldChange={onSectionTextFieldChange}
          onSectionTextFieldBlur={onSectionTextFieldBlur}
          isLoading={isLoading}
          onLinkClick={onLinkClick}
          embedHtmlMap={embedHtmlMap}
          embedInitDataMap={embedInitDataMap}
          onSectionEmbedNavigate={onSectionEmbedNavigate}
          onSectionEmbedBack={onSectionEmbedBack}
          onSectionEmbedTrackEvent={onSectionEmbedTrackEvent}
          onSectionEmbedValueChange={onSectionEmbedValueChange}
          onSectionEmbedSetVariable={onSectionEmbedSetVariable}
          onSectionEmbedTriggerOptionAction={onSectionEmbedTriggerOptionAction}
          onSectionEmbedTriggerButtonAction={onSectionEmbedTriggerButtonAction}
          onSectionEmbedSelectProduct={onSectionEmbedSelectProduct}
          onSectionEmbedPurchaseProduct={onSectionEmbedPurchaseProduct}
          sectionEmbedPurchaseResult={sectionEmbedPurchaseResult}
          lastVariableUpdate={lastVariableUpdate}
        />
      );

    default:
      return (
        <div className={styles.fallback} data-testid="view-screen-fallback">
          Unsupported step type: {step.screenType}
        </div>
      );
  }
  })();

  return (
    <ScreenAnimationProvider
      content={step.content}
      screenType={step.screenType}
      locale={locale}
      defaultScreenAnimation={defaultScreenAnimation}
      previewMode={previewMode}
      ssrMode={ssrMode}
      isAnimationPlaying={isAnimationPlaying}
      onAnimationComplete={onAnimationComplete}
      onEntranceComplete={onEntranceComplete}
      screenEntered={screenEntered}
      skipMountEntrances={skipMountEntrances}
      ssrEntrance={ssrEntrance}
    >
      {screen}
    </ScreenAnimationProvider>
  );
});

V2ScreenView.displayName = 'V2ScreenView';
